import React from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Card, CardHeader, Container, ConfirmationDialog, DataGrid, Icon, NavBar } from "../../shared/components";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../store";
import * as metastoreApi from "../../shared/metastore-api-client";
import { buildSnapshotLink, formatSnapshotSize, formatSnapshotTimestamp, SnapshotMetaV1 } from "../../store/storyline/snapshots";
import { SharedWithAutocomplete } from "../actions/snapshots/SharedWithAutocomplete";
import { useAppActions } from "../../shared/hooks";
import { Alert as AlertIcon } from "../../shared/components/icons";

function ShareSnapshotDialog(props: { snapshot: SnapshotMetaV1, open: boolean, setOpen: (open: boolean) => void, onSuccess: () => void }) {
    const { snapshot, open, setOpen, onSuccess } = props;
    const { showSuccess, showError } = useAppActions();
    const metastoreUrl = useSelector((state: RootState) => state.app.settings.metastoreUrl);
    const [sharedWith, setSharedWith] = React.useState<string[]>(snapshot?.sharedWith ?? []);

    const save = () => {
        new metastoreApi.SnapshotsClient(metastoreUrl)
            .updateSharing(snapshot.owner, snapshot.id, sharedWith)
            .then(() => {
                showSuccess("Snapshot sharing updated.");
                setOpen(false);
                onSuccess();
            })
            .catch(() => showError("Unable to update snapshot sharing.  Please try again."));
    };

    return (
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>Share snapshot{snapshot?.name ? ` | ${snapshot.name}` : ""}</DialogTitle>
            <DialogContent>
                <SharedWithAutocomplete value={sharedWith} onChange={setSharedWith} />
                {
                    sharedWith && sharedWith.length > 0 &&
                    <Alert color="error" icon={<AlertIcon />}>
                        These users will be able to view the data contained within this snapshot.  Please double-check that no sensitive business information is inadvertently being shared with the wrong people.
                    </Alert>
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} variant="contained" color="primary">Save</Button>
            </DialogActions>
        </Dialog>
    );
}

type SnapshotScope = "owned" | "shared";

function MySnapshots() {
    const { showSuccess, showError } = useAppActions();
    const metastoreUrl = useSelector((state: RootState) => state.app.settings.metastoreUrl);
    const navigate = useNavigate();
    const [scope, setScope] = React.useState<SnapshotScope>("owned");
    const [refreshCounter, setRefreshCounter] = React.useState(0);
    const [data, setData] = React.useState<SnapshotMetaV1[]>([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [activeItem, setActiveItem] = React.useState<SnapshotMetaV1>(null);
    const [shareOpen, setShareOpen] = React.useState(false);
    const [confirmOpen, setConfirmOpen] = React.useState(false);

    // Keyed on scope so the shared-with-me hive scan only runs when that tab is opened...
    React.useEffect(() => {
        setIsLoading(true);
        setData([]);
        new metastoreApi.SnapshotsClient(metastoreUrl).getAll(scope)
            .then(result => setData(result ?? []))
            .catch(() => showError("Unable to load your snapshots.  Please try again."))
            .finally(() => setIsLoading(false));
    }, [scope, refreshCounter]);

    const getLink = (snapshot: SnapshotMetaV1) =>
        buildSnapshotLink(snapshot.storylineName, snapshot.owner, snapshot.id, window.location.origin);

    const copyLink = (snapshot: SnapshotMetaV1) => {
        navigator.clipboard?.writeText(getLink(snapshot))
            .then(() => showSuccess("Snapshot link copied to clipboard."))
            .catch(() => showError("Unable to copy the link to the clipboard."));
    };

    const openSnapshot = (snapshot: SnapshotMetaV1) => {
        const url = new URL(getLink(snapshot));
        navigate(`${url.pathname}${url.search}`);
    };

    const shareSnapshot = (snapshot: SnapshotMetaV1) => {
        setActiveItem(snapshot);
        setShareOpen(true);
    };

    const deleteSnapshot = (snapshot: SnapshotMetaV1) => {
        setActiveItem(snapshot);
        setConfirmOpen(true);
    };

    const onDeleteConfirmed = () => {
        new metastoreApi.SnapshotsClient(metastoreUrl).delete(activeItem.owner, activeItem.id)
            .then(() => {
                showSuccess("Snapshot deleted successfully.");
                setRefreshCounter(refreshCounter + 1);
            })
            .catch(() => showError("Unable to delete snapshot.  Please try again."));
    };

    const sharedColumns = [
        { headerName: "Owner", field: "owner", width: 180 },
    ];
    const ownedColumns = [
        { headerName: "Shared with", field: "sharedWith", width: 110, valueGetter: (value: any) => (value as string[])?.length ?? 0 },
    ];

    return (
        <>
            <NavBar title="My Snapshots"></NavBar>

            <Container className="plush snapshots-list" maxWidth="lg">
                <Card className="col-fill">
                    <CardHeader
                        title={scope === "owned" ? "Snapshots you created" : "Snapshots shared with you"}
                        action={
                            <ToggleButtonGroup
                                size="small"
                                color="secondary"
                                exclusive
                                value={scope}
                                onChange={(_event, newScope: SnapshotScope) => newScope && setScope(newScope)}
                            >
                                <ToggleButton value="owned">Created by me</ToggleButton>
                                <ToggleButton value="shared">Shared with me</ToggleButton>
                            </ToggleButtonGroup>
                        }
                    />
                    <DataGrid
                        disableRowSelectionOnClick
                        loading={isLoading}
                        columns={[
                            {
                                headerName: "Name", field: "name", flex: 1,
                                valueGetter: (value) => value ?? "(untitled)",
                                renderCell: (e) => <Link component="button" onClick={() => openSnapshot(e.row)}>{e.value}</Link>
                            },
                            { headerName: "Storyline", field: "storylineName", flex: 1 },
                            {
                                headerName: "Captured", field: "capturedAt", width: 180,
                                valueGetter: (value) => value ? new Date(value as any) : null,
                                renderCell: (e) => e.row.capturedAt ? formatSnapshotTimestamp(e.row.capturedAt) : ""
                            },
                            { headerName: "Size", field: "sizeBytes", width: 110, renderCell: (e) => formatSnapshotSize(e.row.sizeBytes ?? 0) },
                            ...(scope === "owned" ? ownedColumns : sharedColumns),
                            {
                                field: "actions", width: 150, type: "actions", getActions: (r) => scope === "owned" ?
                                    [
                                        <Button size="small" variant="contained" color="primary" onClick={(e) => { copyLink(r.row); e.preventDefault(); }} title="Copy Link"><Icon fontSize="small">link</Icon></Button>,
                                        <Button size="small" variant="contained" color="primary" onClick={(e) => { shareSnapshot(r.row); e.preventDefault(); }} title="Share Snapshot"><Icon fontSize="small">share</Icon></Button>,
                                        <Button size="small" variant="contained" color="error" onClick={(e) => { deleteSnapshot(r.row); e.preventDefault(); }} title="Delete Snapshot"><Icon fontSize="small">delete</Icon></Button>
                                    ] :
                                    [
                                        <Button size="small" variant="contained" color="primary" onClick={(e) => { openSnapshot(r.row); e.preventDefault(); }} title="Open Snapshot"><Icon fontSize="small">open_in_new</Icon></Button>,
                                        <Button size="small" variant="contained" color="primary" onClick={(e) => { copyLink(r.row); e.preventDefault(); }} title="Copy Link"><Icon fontSize="small">link</Icon></Button>
                                    ]
                            }
                        ]}
                        rows={data}
                        initialState={{
                            sorting: {
                                sortModel: [
                                    { field: "capturedAt", sort: "desc" }
                                ]
                            }
                        }}
                    />
                </Card>
            </Container>

            {
                shareOpen &&
                <ShareSnapshotDialog
                    key={activeItem?.id}
                    snapshot={activeItem}
                    open={shareOpen}
                    setOpen={setShareOpen}
                    onSuccess={() => setRefreshCounter(refreshCounter + 1)}
                />
            }

            <ConfirmationDialog
                title="Delete Snapshot?"
                open={confirmOpen}
                setOpen={setConfirmOpen}
                onConfirm={onDeleteConfirmed}
            >
                {`Are you sure you want to delete this snapshot${activeItem?.name ? ` ("${activeItem.name}")` : ""}?  Anyone holding its link will lose access.`}
            </ConfirmationDialog>
        </>
    );
}

export default MySnapshots;
